import { useState, useEffect } from 'react'
import {
  Download,
  TrendingUp,
  Users,
  Video,
  DollarSign,
  BarChart3,
  Star,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  ClipboardList,
} from 'lucide-react'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { adminApi } from '../../lib/api/admin'

interface RevenueReport {
  total_revenue: number
  platform_revenue: number
  interviewer_payouts: number
  session_count: number
  by_month: MonthlyRevenue[]
}

interface MonthlyRevenue {
  month: string
  total_revenue: number
  platform_revenue: number
  interviewer_payouts: number
  session_count: number
  new_clients: number
}

interface InterviewerStat {
  alias: string
  total_sessions: number
  sessions_completed: number
  average_rating: number | null
  total_earnings: number
  cancel_rate: number
  avg_session_length_minutes: number | null
  total_hours: number
}

interface DashboardStats {
  total_users: number
  active_sessions: number
  total_sessions: number
  total_evaluations: number
  avg_session_rating: number | null
}

interface SessionCounts {
  upcoming: number
  completed: number
  pending_evaluation: number
  rescheduled: number
  cancelled: number
  total: number
}

const fmt = (n: number) =>
  '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const fmtRating = (r: number | null) => (r != null ? r.toFixed(1) : '—')

// Maps frontend dateRange values to the backend /admin/stats `range` param format
const RANGE_KEY_MAP: Record<string, string> = {
  today: 'today',
  week: '7d',
  month: '30d',
  quarter: '90d',
  year: '1y',
}

function getRangeDates(range: string): { from_date: string; to_date: string } | Record<string, never> {
  const now = new Date()
  const toDate = new Date(now)
  toDate.setDate(toDate.getDate() + 1)
  const toStr = toDate.toISOString().slice(0, 10)

  const fromDate = new Date(now)
  if (range === 'today') {
    fromDate.setHours(0, 0, 0, 0)
  } else if (range === 'week') {
    fromDate.setDate(fromDate.getDate() - 7)
  } else if (range === 'month') {
    fromDate.setDate(fromDate.getDate() - 30)
  } else if (range === 'quarter') {
    fromDate.setDate(fromDate.getDate() - 90)
  } else if (range === 'year') {
    fromDate.setDate(fromDate.getDate() - 365)
  } else {
    return {}
  }
  return { from_date: fromDate.toISOString().slice(0, 10), to_date: toStr }
}


export default function AdminReports() {
  const [dateRange, setDateRange] = useState('month')
  const [activeTab, setActiveTab] = useState('overview')
  const [loading, setLoading] = useState(true)

  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [revenue, setRevenue] = useState<RevenueReport | null>(null)
  const [interviewers, setInterviewers] = useState<InterviewerStat[]>([])
  const [sessionCounts, setSessionCounts] = useState<SessionCounts | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const rangeDates = getRangeDates(dateRange)
        const rangeKey = RANGE_KEY_MAP[dateRange]
        const [statsRes, revenueRes, interviewerRes, countsRes] = await Promise.all([
          adminApi.getStats(rangeKey ? { range: rangeKey } : {}).catch(() => null),
          adminApi.getRevenueReport(rangeDates).catch(() => null),
          adminApi.getInterviewerReport(rangeDates).catch(() => null),
          adminApi.getSessionCounts(rangeDates).catch(() => null),
        ])

        if (statsRes) setStats(statsRes)
        if (revenueRes) {
          const r = revenueRes.data ?? revenueRes
          setRevenue(r)
        }
        if (interviewerRes) {
          const list = interviewerRes.data ?? interviewerRes
          setInterviewers(Array.isArray(list) ? list : [])
        }
        if (countsRes) setSessionCounts(countsRes)
      } catch (err) {
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [dateRange])

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const pageW = doc.internal.pageSize.getWidth()
    const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    const navy = [30, 36, 50] as [number, number, number]
    const gold = [212, 175, 55] as [number, number, number]

    // ── Header ──────────────────────────────────────────────────────────────
    doc.setFillColor(...navy)
    doc.rect(0, 0, pageW, 28, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.text('Reports & Analytics', 14, 12)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.text(`Generated: ${now}  |  Range: ${dateRange}`, 14, 20)

    let y = 36

    // ── KPI Summary ──────────────────────────────────────────────────────────
    doc.setTextColor(...navy)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('Summary', 14, y)
    y += 4

    const kpis = [
      ['Total Sessions', String(sessionCounts?.total ?? 0)],
      ['Revenue Generated', revenue ? fmt(revenue.total_revenue) : '—'],
      ['Avg Session Rating', fmtRating(stats?.avg_session_rating ?? null)],
      ['Total Users', String(stats?.total_users ?? 0)],
    ]
    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value']],
      body: kpis,
      theme: 'grid',
      headStyles: { fillColor: navy, textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: { 1: { fontStyle: 'bold' } },
      margin: { left: 14, right: 14 },
    })
    y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6

    // ── Session Status Breakdown ─────────────────────────────────────────────
    if (sessionCounts) {
      doc.setTextColor(...navy)
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('Session Status Breakdown', 14, y)
      y += 4
      autoTable(doc, {
        startY: y,
        head: [['Upcoming', 'Completed', 'Pending Eval', 'Rescheduled', 'Cancelled', 'Total']],
        body: [[
          sessionCounts.upcoming,
          sessionCounts.completed,
          sessionCounts.pending_evaluation,
          sessionCounts.rescheduled,
          sessionCounts.cancelled,
          sessionCounts.total,
        ]],
        theme: 'grid',
        headStyles: { fillColor: navy, textColor: [255, 255, 255], fontStyle: 'bold' },
        margin: { left: 14, right: 14 },
      })
      y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6
    }

    // ── Revenue & Payouts ────────────────────────────────────────────────────
    if (interviewers.length) {
      doc.setTextColor(...navy)
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('Revenue & Payouts', 14, y)
      y += 4
      autoTable(doc, {
        startY: y,
        head: [['Interviewer', 'Completed Sessions', 'Avg Rating', 'Revenue Generated']],
        body: interviewers.map((i) => [
          i.alias,
          i.total_sessions,
          i.average_rating != null ? i.average_rating.toFixed(1) : '—',
          fmt(i.total_earnings),
        ]),
        theme: 'striped',
        headStyles: { fillColor: navy, textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        margin: { left: 14, right: 14 },
      })
      y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6
    }

    // ── Monthly Overview ─────────────────────────────────────────────────────
    if (revenue?.by_month?.length) {
      if (y > 220) { doc.addPage(); y = 20 }
      doc.setTextColor(...navy)
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('Monthly Overview', 14, y)
      y += 4
      const monthlyBody = revenue.by_month.map((r) => [
        r.month, r.session_count, fmt(r.total_revenue), r.new_clients,
      ])
      const totalSessions = revenue.by_month.reduce((s, r) => s + (r.session_count ?? 0), 0)
      const totalClients = revenue.by_month.reduce((s, r) => s + (r.new_clients ?? 0), 0)
      autoTable(doc, {
        startY: y,
        head: [['Month', 'Total Sessions', 'Revenue', 'New Clients']],
        body: monthlyBody,
        foot: [['Total', totalSessions, fmt(revenue.total_revenue), totalClients]],
        theme: 'striped',
        headStyles: { fillColor: navy, textColor: [255, 255, 255], fontStyle: 'bold' },
        footStyles: { fillColor: gold, textColor: [30, 36, 50], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        margin: { left: 14, right: 14 },
      })
      y = (doc as jsPDF & { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6
    }

    // ── Interviewer Performance Metrics ──────────────────────────────────────
    if (interviewers.length) {
      if (y > 200) { doc.addPage(); y = 20 }
      doc.setTextColor(...navy)
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.text('Interviewer Performance Metrics', 14, y)
      y += 4
      autoTable(doc, {
        startY: y,
        head: [['Interviewer', 'Sessions Completed', 'Cancel Rate', 'Avg Session Length', 'Total Hours']],
        body: interviewers.map((i) => [
          i.alias,
          i.sessions_completed,
          `${i.cancel_rate}%`,
          i.avg_session_length_minutes != null ? `${i.avg_session_length_minutes} min` : '—',
          i.total_hours > 0 ? `${i.total_hours}h` : '—',
        ]),

        theme: 'striped',
        headStyles: { fillColor: navy, textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        margin: { left: 14, right: 14 },
      })
    }

    // ── Footer on all pages ──────────────────────────────────────────────────
    const totalPages = doc.internal.pages.length - 1
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)
      doc.setFontSize(8)
      doc.setTextColor(150)
      doc.text(
        `Interview Dress Rehearsal  |  Page ${i} of ${totalPages}`,
        pageW / 2,
        doc.internal.pageSize.getHeight() - 8,
        { align: 'center' }
      )
    }

    doc.save(`idr-report-${new Date().toISOString().slice(0, 10)}.pdf`)
  }

  if (loading) {
    return <div className="loading-screen">Loading reports...</div>
  }

  return (
    <div>
      {/* Header */}
      <div
        className="page-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}
      >
        <div>
          <h1 className="page-title">Reports & Analytics</h1>
          <p className="page-subtitle">Performance insights and financial overview</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <select
            className="form-input"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            style={{ width: 'auto', padding: '8px 12px' }}
          >
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
          <button className="btn btn-primary" onClick={handleExportPDF}>
            <Download size={16} />
            Export PDF
          </button>
        </div>
      </div>

      {/* KPI Stat Cards — order: Total Sessions, Total Users, Revenue Generated, Avg Session Rating */}
      <div className="stats-grid ar-stats-grid" style={{ marginBottom: 24 }}>
        <div className="stat-card">
          <div className="icon primary">
            <Video size={22} />
          </div>
          <div className="value">{sessionCounts?.total ?? 0}</div>
          <div className="label">Total Sessions</div>
        </div>
        <div className="stat-card">
          <div className="icon primary">
            <Users size={22} />
          </div>
          <div className="value">{stats?.total_users ?? 0}</div>
          <div className="label">Total Users</div>
        </div>
        <div className="stat-card">
          <div className="icon success">
            <DollarSign size={22} />
          </div>
          <div className="value">{revenue ? fmt(revenue.total_revenue) : '—'}</div>
          <div className="label">Revenue Generated</div>
        </div>
        <div className="stat-card">
          <div className="icon success">
            <Star size={22} />
          </div>
          <div className="value">{fmtRating(stats?.avg_session_rating ?? null)}</div>
          <div className="label">Avg Session Rating</div>
        </div>
      </div>

      {/* Session Status Breakdown */}
      {sessionCounts && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 12,
            marginBottom: 28,
          }}
        >
          {[
            { label: 'Upcoming', value: sessionCounts.upcoming, icon: <Clock size={16} />, color: 'var(--primary)' },
            { label: 'Completed', value: sessionCounts.completed, icon: <CheckCircle size={16} />, color: 'var(--success)' },
            { label: 'Pending Eval', value: sessionCounts.pending_evaluation, icon: <ClipboardList size={16} />, color: '#d4af37' },
            { label: 'Rescheduled', value: sessionCounts.rescheduled, icon: <RefreshCw size={16} />, color: 'var(--warning)' },
            { label: 'Cancelled', value: sessionCounts.cancelled, icon: <XCircle size={16} />, color: 'var(--danger)' },
          ].map(({ label, value, icon, color }) => (
            <div
              key={label}
              className="card"
              style={{ padding: '14px 16px', textAlign: 'center' }}
            >
              <div style={{ color, marginBottom: 6, display: 'flex', justifyContent: 'center' }}>
                {icon}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>
                {value}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 24 }}>
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BarChart3 size={16} />
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'interviewer-performance' ? 'active' : ''}`}
          onClick={() => setActiveTab('interviewer-performance')}
        >
          <TrendingUp size={16} />
          Interviewer Performance
        </button>
      </div>

      {/* ── Overview Tab ── */}
      {activeTab === 'overview' && (
        <div className="ar-overview-grid">

          {/* Revenue & Payouts card */}
          <div className="card">
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600 }}>Revenue & Payouts</h3>
            </div>
            <div className="table-container ar-revenue-table" style={{ maxHeight: 320, overflowY: 'auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch' as 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ position: 'sticky', top: 0, background: 'var(--card-bg, #1e2432)', zIndex: 1 }}>Interviewer</th>
                    <th style={{ position: 'sticky', top: 0, background: 'var(--card-bg, #1e2432)', zIndex: 1 }}>Completed Sessions</th>
                    <th style={{ position: 'sticky', top: 0, background: 'var(--card-bg, #1e2432)', zIndex: 1 }}>Avg Rating</th>
                    <th style={{ position: 'sticky', top: 0, background: 'var(--card-bg, #1e2432)', zIndex: 1 }}>Revenue Generated</th>
                  </tr>
                </thead>
                <tbody>
                  {interviewers.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                        No data available for this period
                      </td>
                    </tr>
                  ) : (
                    interviewers.map((iw) => (
                      <tr key={iw.alias}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div className="avatar">{iw.alias.charAt(0).toUpperCase()}</div>
                            <span style={{ fontWeight: 500 }}>{iw.alias}</span>
                          </div>
                        </td>
                        <td>{iw.total_sessions}</td>
                        <td>
                          {iw.average_rating != null ? (
                            <span style={{ color: '#d4af37', fontWeight: 600 }}>
                              ★ {iw.average_rating.toFixed(1)}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-muted)' }}>—</span>
                          )}
                        </td>
                        <td style={{ color: 'var(--success)', fontWeight: 600 }}>
                          {fmt(iw.total_earnings)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Monthly Overview card */}
          <div className="card">
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 18, fontWeight: 600 }}>Monthly Overview</h3>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Total Sessions</th>
                    <th>Revenue</th>
                    <th>New Clients</th>
                  </tr>
                </thead>
                <tbody>
                  {!revenue?.by_month?.length ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                        No data available for this period
                      </td>
                    </tr>
                  ) : (
                    revenue.by_month.map((row) => (
                      <tr key={row.month}>
                        <td style={{ fontWeight: 600 }}>{row.month}</td>
                        <td>{row.session_count}</td>
                        <td style={{ fontWeight: 600, color: 'var(--success)' }}>
                          {fmt(row.total_revenue)}
                        </td>
                        <td>{row.new_clients}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {revenue?.by_month?.length ? (
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 700 }}>
                      <td>Total</td>
                      <td>{revenue.by_month.reduce((s, r) => s + (r.session_count ?? 0), 0)}</td>
                      <td style={{ color: 'var(--success)' }}>{fmt(revenue.total_revenue)}</td>
                      <td>
                        {revenue.by_month.reduce((s, r) => s + (r.new_clients ?? 0), 0)}
                      </td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Interviewer Performance Tab ── */}
      {activeTab === 'interviewer-performance' && (
        <div className="card">
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>Interviewer Performance Metrics</h3>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Interviewer</th>
                  <th>Sessions Completed</th>
                  <th>Cancel Rate</th>
                  <th>Avg Session Length</th>
                  <th>Total Hours</th>
                </tr>
              </thead>
              <tbody>
                {interviewers.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                      No interviewer data available
                    </td>
                  </tr>
                ) : (
                  interviewers.map((iw) => (
                    <tr key={iw.alias}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div className="avatar">{iw.alias.charAt(0).toUpperCase()}</div>
                          <span style={{ fontWeight: 500 }}>{iw.alias}</span>
                        </div>
                      </td>
                      <td>{iw.sessions_completed}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div
                            style={{
                              width: 50,
                              height: 6,
                              background: 'var(--border)',
                              borderRadius: 3,
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                height: '100%',
                                width: `${Math.min(iw.cancel_rate, 100)}%`,
                                background: iw.cancel_rate > 20 ? 'var(--danger)' : 'var(--warning)',
                                borderRadius: 3,
                              }}
                            />
                          </div>
                          <span
                            style={{
                              fontSize: 13,
                              color: iw.cancel_rate > 20 ? 'var(--danger)' : 'var(--text-primary)',
                              fontWeight: iw.cancel_rate > 20 ? 600 : 400,
                            }}
                          >
                            {iw.cancel_rate}%
                          </span>
                        </div>
                      </td>
                      <td>
                        {iw.avg_session_length_minutes != null
                          ? `${iw.avg_session_length_minutes} min`
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={{ fontWeight: 500 }}>
                        {iw.total_hours > 0 ? `${iw.total_hours}h` : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <style>{`
        /* KPI stats: 4-col on PC/tablet, 2×2 on mobile */
        .ar-stats-grid { grid-template-columns: repeat(4, 1fr) !important; }
        @media (max-width: 640px) {
          .ar-stats-grid { grid-template-columns: repeat(2, 1fr) !important; gap: 12px !important; }
        }
        @media (max-width: 480px) {
          .ar-stats-grid { gap: 10px !important; }
          .ar-stats-grid .stat-card { padding: 14px 12px !important; }
          .ar-stats-grid .icon { width: 38px !important; height: 38px !important; }
          .ar-stats-grid .value { font-size: 20px !important; }
        }

        /* Overview grid: prevent cards from expanding past viewport */
        .ar-overview-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          align-items: start;
        }
        .ar-overview-grid > * { min-width: 0; overflow: hidden; }

        @media (max-width: 768px) {
          .ar-overview-grid { grid-template-columns: 1fr; gap: 16px; }
        }

        /* Revenue & Payouts table: ensure horizontal scroll works inside card */
        .ar-revenue-table { overflow-x: auto !important; -webkit-overflow-scrolling: touch; }
        .ar-revenue-table table { min-width: 400px; }
      `}</style>
    </div>
  )
}
